import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { keyframes } from "@emotion/react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const OPTIONS = [
  { id: "both", accent: "#0f766e" },
  { id: "front", accent: "#1473e6" },
  { id: "back", accent: "#475569" },
];

const pop = keyframes`
  0%, 10% { opacity: 0; transform: translateY(8px) scale(0.96); }
  28%, 72% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0; transform: translateY(4px) scale(0.98); }
`;

const flip = keyframes`
  0%, 12% { transform: rotateY(0deg); }
  40%, 60% { transform: rotateY(180deg); }
  100% { transform: rotateY(0deg); }
`;

const slidePair = keyframes`
  0%, 15% { opacity: 0.35; transform: scale(0.92); }
  35%, 65% { opacity: 1; transform: scale(1); }
  100% { opacity: 0.35; transform: scale(0.92); }
`;

const draw = keyframes`
  0%, 8% { stroke-dashoffset: 160; }
  40%, 75% { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: 0; }
`;

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const JACKET =
  "M10 16 L16 8 H21 L24 13 L27 8 H32 L38 16 L44 24 L36 27 V50 H12 V27 L4 24 Z";

function Stage({ children, tint }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 92,
        overflow: "hidden",
        bgcolor: tint,
        perspective: 600,
      }}
    >
      {children}
    </Box>
  );
}

function JacketSvg({ fill = "#1e293b", strokeAnim }) {
  return (
    <Box component="svg" viewBox="0 0 48 56" sx={{ width: 34, height: 42 }}>
      <path
        d={JACKET}
        fill={fill}
        stroke={strokeAnim ? "#0f172a" : "none"}
        strokeWidth={strokeAnim ? 1.2 : 0}
        strokeDasharray={strokeAnim ? 220 : undefined}
        style={
          strokeAnim
            ? { animation: `${draw} 4.6s ease-in-out infinite`, ...motionSafe }
            : undefined
        }
      />
    </Box>
  );
}

function BothPreview() {
  return (
    <Stage tint="#eef7f5">
      {["#0f766e", "#134e4a"].map((tone, i) => (
        <Box
          key={tone}
          sx={{
            position: "absolute",
            left: 18 + i * 52,
            top: 18,
            width: 44,
            height: 58,
            borderRadius: 1,
            bgcolor: "#fff",
            border: "1px solid #cfe3de",
            display: "grid",
            placeItems: "center",
            animation: `${slidePair} 4.6s ease-in-out infinite`,
            animationDelay: `${i * 0.2}s`,
            ...motionSafe,
          }}
        >
          <JacketSvg fill={tone} />
          <Box
            sx={{
              position: "absolute",
              bottom: 3,
              fontSize: "0.55rem",
              fontWeight: 800,
              color: "#0f766e",
              letterSpacing: 0.4,
            }}
          >
            {i === 0 ? "F" : "B"}
          </Box>
        </Box>
      ))}
    </Stage>
  );
}

function FrontPreview() {
  return (
    <Stage tint="#eef4fc">
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: 16,
          width: 48,
          height: 62,
          transform: "translateX(-50%)",
          borderRadius: 1,
          bgcolor: "#fff",
          border: "1px solid #d5e3f7",
          display: "grid",
          placeItems: "center",
          animation: `${pop} 4.6s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <JacketSvg fill="#1473e6" strokeAnim />
      </Box>
    </Stage>
  );
}

function BackPreview() {
  return (
    <Stage tint="#f1f5f9">
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: 16,
          width: 48,
          height: 62,
          ml: "-24px",
          borderRadius: 1,
          bgcolor: "#fff",
          border: "1px solid #e2e8f0",
          display: "grid",
          placeItems: "center",
          transformStyle: "preserve-3d",
          animation: `${flip} 4.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <JacketSvg fill="#475569" />
      </Box>
    </Stage>
  );
}

const PREVIEWS = {
  both: BothPreview,
  front: FrontPreview,
  back: BackPreview,
};

function ViewCard({ id, accent, selected, onSelect }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const Preview = PREVIEWS[id];
  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      sx={{
        p: 0,
        textAlign: "left",
        cursor: "pointer",
        borderRadius: 2.5,
        overflow: "hidden",
        border: "2px solid",
        borderColor: selected ? accent : "divider",
        bgcolor: "background.paper",
        boxShadow: selected ? `0 8px 22px ${alpha(accent, 0.16)}` : "none",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        font: "inherit",
        color: "inherit",
        "&:hover": { borderColor: selected ? accent : alpha(accent, 0.45) },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        <Preview />
        {selected ? (
          <CheckCircleIcon
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              color: accent,
              bgcolor: "#fff",
              borderRadius: "50%",
              fontSize: 20,
            }}
          />
        ) : null}
      </Box>
      <Box sx={{ px: 1.25, py: 1.15 }}>
        <Typography fontWeight={800} fontSize="0.95rem">
          {t(`presentacionesSearch.newDesign.sketchView.${id}.title`)}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.35, lineHeight: 1.4 }}
        >
          {t(`presentacionesSearch.newDesign.sketchView.${id}.body`)}
        </Typography>
      </Box>
    </Box>
  );
}

export default function NuevoDisenoSketchViewDialog({
  open,
  onClose,
  onConfirm,
  submitting,
}) {
  const { t } = useTranslation();
  const [choice, setChoice] = useState("both");

  useEffect(() => {
    if (open) setChoice("both");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose?.()}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={800}>
          {t("presentacionesSearch.newDesign.sketchView.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t("presentacionesSearch.newDesign.sketchView.lead")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            pt: 1.25,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
            gap: 1.25,
          }}
        >
          {OPTIONS.map((opt) => (
            <ViewCard
              key={opt.id}
              id={opt.id}
              accent={opt.accent}
              selected={choice === opt.id}
              onSelect={() => setChoice(opt.id)}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={submitting}>
          {t("presentacionesSearch.newDesign.sketchView.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={submitting || !choice}
          onClick={() => onConfirm?.(choice)}
          sx={{
            borderRadius: 999,
            fontWeight: 700,
            textTransform: "none",
            px: 2.5,
            bgcolor: "#1473e6",
            "&:hover": { bgcolor: "#0d66d0" },
          }}
        >
          {submitting
            ? t("presentacionesSearch.newDesign.generating")
            : t("presentacionesSearch.newDesign.sketchView.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
